import { Injectable } from '@angular/core';
import { ConnectionService, ConnectionType } from './connection-service';
import { ExternalResourcesService } from './external-resources.service';

interface AssetConfig {
  url: string;
  priority: 'high' | 'medium' | 'low';
  type: 'script' | 'style';
}

@Injectable({
  providedIn: 'root'
})
export class AssetLoaderService {
  // Liste des assets non essentiels qui peuvent être chargés à la demande
  private nonEssentialAssets: AssetConfig[] = [
    // TODO: Ajouter ici les ressources externes réelles quand nécessaire
    // Exemple: { url: 'assets/js/animations.js', priority: 'low', type: 'script' }
  ];

  constructor(
    private connectionService: ConnectionService,
    private externalResourcesService: ExternalResourcesService
  ) {}

  /**
   * Initialise le chargement intelligent des assets en fonction de la connexion
   */
  initializeAssetLoading(): void {
    // Observez les changements de qualité de connexion
    this.connectionService.getConnectionType().subscribe(connectionType => {
      this.loadAssetsBasedOnConnection(connectionType);
    });
  }

  /**
   * Charge les assets appropriés selon la qualité de connexion
   */
  private loadAssetsBasedOnConnection(connectionType: ConnectionType): void {
    switch (connectionType) {
      case ConnectionType.FAST:
        // Sur connexion rapide, charger tous les assets
        this.loadAllAssets();
        break;

      case ConnectionType.MEDIUM:
        // Sur connexion moyenne, charger les assets de priorité haute et moyenne
        this.loadMediumPriorityAssets();
        break;

      case ConnectionType.SLOW:
        // Sur connexion lente, ne charger que les assets essentiels
        this.loadHighPriorityAssets();
        break;
    }
  }

  /**
   * Charge tous les assets, quelle que soit leur priorité
   */
  private loadAllAssets(): void {
    this.nonEssentialAssets.forEach(asset => {
      if (asset.type === 'script') {
        this.externalResourcesService.loadScript(asset.url, false).catch(err => {
          console.warn(`Échec chargement script non essentiel: ${asset.url}`, err);
        });
      } else {
        this.externalResourcesService.loadStylesheet(asset.url, false).catch(err => {
          console.warn(`Échec chargement CSS non essentiel: ${asset.url}`, err);
        });
      }
    });
  }

  /**
   * Charge uniquement les assets de priorité haute et moyenne
   */
  private loadMediumPriorityAssets(): void {
    this.nonEssentialAssets
      .filter(asset => asset.priority !== 'low')
      .forEach(asset => {
        if (asset.type === 'script') {
          this.externalResourcesService.loadScript(asset.url, false).catch(err => {
            console.warn(`Échec chargement script priorité moyenne: ${asset.url}`, err);
          });
        } else {
          this.externalResourcesService.loadStylesheet(asset.url, false).catch(err => {
            console.warn(`Échec chargement CSS priorité moyenne: ${asset.url}`, err);
          });
        }
      });
  }

  /**
   * Charge uniquement les assets de haute priorité
   */
  private loadHighPriorityAssets(): void {
    this.nonEssentialAssets
      .filter(asset => asset.priority === 'high')
      .forEach(asset => {
        if (asset.type === 'script') {
          this.externalResourcesService.loadScript(asset.url, true).catch(err => {
            console.warn(`Échec chargement script haute priorité: ${asset.url}`, err);
          });
        } else {
          this.externalResourcesService.loadStylesheet(asset.url, true).catch(err => {
            console.warn(`Échec chargement CSS haute priorité: ${asset.url}`, err);
          });
        }
      });
  }

  /**
   * Charge un asset spécifique à la demande
   */
  loadAssetOnDemand(url: string, type: 'script' | 'style'): Promise<void> {
    return type === 'script'
      ? this.externalResourcesService.loadScript(url, true)
      : this.externalResourcesService.loadStylesheet(url, true);
  }
}
